import React, { ReactNode, useEffect, useRef, useState } from "react";
import HelpData from "./HelpData";
import ReactMarkdown from "react-markdown";
import HelpDocHeader from "./HelpDocHeader";
import { Link, useLocation, useParams } from "react-router-dom";
import { FaBars, FaTimes, FaChevronDown, FaChevronUp } from "react-icons/fa";

type Heading = { id: string; text: string; level: string };

const Docs = () => {
    const location = useLocation();
    const [activeLink, setActiveLink] = useState("");
    const [headings, setHeadings] = useState<Heading[]>([]);
    const [content, setContent] = useState("# Loading...");
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [mobileTOCOpen, setMobileTOCOpen] = useState(false);
    const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({});
    const contentRef = useRef<HTMLDivElement | null>(null);
    const mainRef = useRef<HTMLDivElement | null>(null);
    const { slug } = useParams();

    const getTextFromChildren = (children: ReactNode): string => {
        if (typeof children === "string" || typeof children === "number") {
            return children.toString();
        }

        if (Array.isArray(children)) {
            return children.map(getTextFromChildren).join("");
        }

        if (React.isValidElement(children)) {
            return getTextFromChildren(children.props.children);
        }

        return "";
    };

    useEffect(() => {
        const scrollToHash = () => {
            const hash = location.hash;
            const id = decodeURIComponent(hash.slice(1));
            if (id) {
                const element = document.getElementById(id);
                if (element) {
                    element.scrollIntoView({ behavior: "smooth", block: "start" });
                }
            }
        };
        const timeout = setTimeout(scrollToHash, 100);
        return () => clearTimeout(timeout);
    }, [content, location]);

    useEffect(() => {
        setActiveLink(location.pathname);
    }, [location.pathname]);

    useEffect(() => {
        const titleKey = slug?.toLowerCase() || "introduction";
        const filePath = `/docs/${titleKey}.md`;
        fetch(filePath)
            .then((res) => {
                if (!res.ok) throw new Error("Not found");
                return res.text();
            })
            .then((text) => setContent(text))
            .catch(() => setContent("# Coming Soon\nThis content is not yet available."));
    }, [slug]);

    useEffect(() => {
        const contentEl = contentRef.current;
        if (contentEl) {
            const foundHeadings: Heading[] = Array.from(
                contentEl.querySelectorAll("h1, h2")
            ).map((el) => ({
                id: el.id,
                text: el.textContent || "",
                level: el.tagName,
            }));
            setHeadings(foundHeadings);
        }
    }, [content]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const tocDropdown = document.getElementById("mobile-toc-dropdown");
            if (tocDropdown && !tocDropdown.contains(event.target as Node) && mobileTOCOpen) {
                setMobileTOCOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [mobileTOCOpen]);

    useEffect(() => {
        if (mainRef.current) {
            mainRef.current.scrollTo({ top: 0, behavior: "auto" });
        }
    }, [slug]);

    return (
        <div className="border-b border-b-primary/40">
            <HelpDocHeader />

            <div className="md:hidden flex items-center justify-between p-4 bg-gray-100 border-b">
                <button
                    onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
                    className="text-2xl text-gray-700 focus:outline-none"
                    aria-label="Toggle Sidebar"
                >
                    {mobileSidebarOpen ? <FaTimes /> : <FaBars />}
                </button>

                <div className="relative">
                    <button
                        className="flex items-center justify-center gap-x-1 text-sm font-semibold text-gray-700"
                        onClick={() => setMobileTOCOpen(!mobileTOCOpen)}
                    >
                        <span>On this page</span>
                        {mobileTOCOpen ? <FaChevronUp /> : <FaChevronDown />}
                    </button>
                    {mobileTOCOpen && (
                        <div id="mobile-toc-dropdown" className="absolute right-0 mt-2 w-64 bg-white border rounded shadow z-10 max-h-60 overflow-y-auto">
                            <ul className="p-2">
                                {headings.map((heading, i) => (
                                    <li key={i} className="text-sm mb-2">
                                        <button
                                            onClick={() => {
                                                const el = document.getElementById(heading.id);

                                                if (el) {
                                                    window.history.replaceState(
                                                        null,
                                                        "",
                                                        `${location.pathname}#${heading.id}`
                                                    );

                                                    el.scrollIntoView({
                                                        behavior: "smooth",
                                                        block: "center",
                                                    });
                                                }
                                            }}
                                            className={`text-left hover:underline hover:text-secondary ${heading.level === "H1"
                                                ? "ml-4 font-semibold"
                                                : "ml-4"
                                                }`}
                                        >
                                            {heading.text}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex flex-col md:flex-row h-screen">
                <aside className="hidden md:block w-64 bg-gray-100 p-4 overflow-y-auto border-r">
                    {HelpData.map((section, i) => (
                        <div key={i} className="mb-6 ml-2">
                            <h3 className="font-bold text-lg text-gray-700 mb-2">{section.category}</h3>
                            <ul>
                                {section.tabs.map((tab, j) => {
                                    const key = `${i}-${j}`;
                                    const hasChildren = Array.isArray(tab.children) && tab.children.length > 0;
                                    const isMatch = (path: string) =>
                                        activeLink === path || activeLink.startsWith(path + "/");
                                    const isChildActive = tab.children?.some((child) =>
                                        isMatch(child.link)
                                    );
                                    const isOpen = openDropdowns[key] || isChildActive;
                                    return (
                                        <li key={j} className="mb-1">
                                            <div className="flex justify-between items-center pr-2 cursor-pointer"
                                                onClick={() =>
                                                    setOpenDropdowns((prev) => ({ ...prev, [key]: !prev[key] }))
                                                }
                                            >
                                                <Link
                                                    to={`${tab.link}`}
                                                    className={`text-lg block p-1 ${!hasChildren && activeLink === tab.link
                                                        ? "text-secondary font-medium"
                                                        : "text-gray-600 hover:text-secondary"
                                                        }`}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {tab.title}
                                                </Link>
                                                {hasChildren && (
                                                    <button
                                                        onClick={() =>
                                                            setOpenDropdowns((prev) => ({ ...prev, [key]: !prev[key] }))
                                                        }
                                                        className="ml-1 text-gray-500 hover:text-secondary focus:outline-none"
                                                    >
                                                        {isOpen ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
                                                    </button>
                                                )}
                                            </div>

                                            {hasChildren && isOpen && (
                                                <ul className="ml-4 mt-1">
                                                    {tab.children!.map((child, k) => (
                                                        <li key={k}>
                                                            <Link
                                                                to={`${child.link}`}
                                                                className={`text-base block p-1 ${isMatch(child.link)
                                                                    ? "text-secondary font-medium"
                                                                    : "text-gray-500 hover:text-secondary"
                                                                    }`}
                                                            >
                                                                {child.title}
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}
                </aside>

                {mobileSidebarOpen && (
                    <aside className="md:hidden bg-gray-100 p-4 border-b w-full">
                        {HelpData.map((section, i) => (
                            <div key={i} className="mb-4">
                                <h3 className="font-bold text-gray-700 mb-2">{section.category}</h3>
                                <ul>
                                    {section.tabs.map((tab, j) => {
                                        const key = `m-${i}-${j}`;
                                        const hasChildren = Array.isArray(tab.children) && tab.children.length > 0;
                                        const isOpen = !openDropdowns[key];

                                        return (
                                            <li key={j}>
                                                <div className="flex justify-between items-center pr-2"
                                                    onClick={() =>
                                                        setOpenDropdowns((prev) => ({ ...prev, [key]: !prev[key] }))
                                                    }
                                                >
                                                    <Link
                                                        to={`${tab.link}`}
                                                        className={`text-base block p-1 ${!hasChildren && activeLink === tab.link
                                                            ? "text-secondary font-medium"
                                                            : "text-gray-600 hover:text-secondary"
                                                            }`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setMobileSidebarOpen(false);
                                                        }}
                                                    >
                                                        {tab.title}
                                                    </Link>
                                                    {hasChildren && (
                                                        <button
                                                            onClick={() =>
                                                                setOpenDropdowns((prev) => ({ ...prev, [key]: !prev[key] }))
                                                            }
                                                            className="ml-1 text-gray-500 hover:text-secondary focus:outline-none"
                                                        >
                                                            {isOpen ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
                                                        </button>
                                                    )}
                                                </div>

                                                {hasChildren && isOpen && (
                                                    <ul className="ml-4 mt-1">
                                                        {tab.children!.map((child, k) => (
                                                            <li key={k}>
                                                                <Link
                                                                    to={`${child.link}`}
                                                                    className={`text-sm block p-1 ${activeLink === child.link
                                                                        ? "text-secondary font-medium"
                                                                        : "text-gray-500 hover:text-secondary"
                                                                        }`}
                                                                    onClick={() => setMobileSidebarOpen(false)}
                                                                >
                                                                    {child.title}
                                                                </Link>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        ))}
                    </aside>
                )}

                <main ref={mainRef} className="flex-1 p-4 md:p-8 overflow-y-auto">
                    <div ref={contentRef}>
                        <ReactMarkdown
                            components={{
                                h1: ({ children }) => {
                                    const text = getTextFromChildren(children);
                                    const id = text
                                        .toLowerCase()
                                        .trim()
                                        .replace(/[^\w\s-]/g, "")
                                        .replace(/\s+/g, "-");
                                    return <h1 id={id} className="text-5xl font-bold mt-10 mb-6">{children}</h1>;
                                },
                                h2: ({ children }) => {
                                    const text = getTextFromChildren(children);
                                    const id = text
                                        .toLowerCase()
                                        .trim()
                                        .replace(/[^\w\s-]/g, "")
                                        .replace(/\s+/g, "-");
                                    return <h2 id={id} className="text-2xl font-semibold mt-8 mb-4">{children}</h2>;
                                },
                                p: ({ children }) => <p className="mb-4 text-lg leading-relaxed">{children}</p>,
                                ul: ({ children }) => <ul className="list-disc list-inside mt-4 mb-4">{children}</ul>,
                                li: ({ children }) => <li className="mb-2">{children}</li>,
                                img: ({ src, alt }) => {
                                    let width, height;
                                    if (src) {
                                        const url = new URL(src, window.location.origin);
                                        width = url.searchParams.get("width");
                                        if (width) {
                                            height = Math.round(parseInt(width) / 2);
                                        }
                                    }
                                    return (
                                        <div className="my-6">
                                            <img
                                                src={src ? src.split("?")[0] : ""}
                                                alt={alt ?? ""}
                                                width={width ?? undefined}
                                                height={height ?? undefined}
                                                className="max-w-full rounded-lg shadow-md object-cover"
                                                style={{
                                                    width: width ? `${width}px` : "100%",
                                                    height: height ? `${height}px` : "auto"
                                                }}
                                            />
                                        </div>
                                    );
                                }
                            }}
                        >
                            {content}
                        </ReactMarkdown>
                    </div>
                </main>

                <aside className="hidden md:block w-72 px-6 py-4 border-l overflow-y-auto">
                    <h4 className="text-lg font-bold mb-4">On this page</h4>
                    <ul className="space-y-3">
                        {headings.map((heading, i) => (
                            <li key={i} className="text-lg">
                                <button
                                    onClick={() => {
                                        const el = document.getElementById(heading.id);

                                        if (el) {
                                            window.history.replaceState(
                                                null,
                                                "",
                                                `${location.pathname}#${heading.id}`
                                            );

                                            el.scrollIntoView({
                                                behavior: "smooth",
                                                block: "center",
                                            });
                                        }
                                    }}
                                    className={`text-left hover:underline hover:text-secondary ${heading.level === "H1"
                                        ? "ml-4 font-semibold"
                                        : "ml-4"
                                        }`}
                                >
                                    {heading.text}
                                </button>
                            </li>
                        ))}
                    </ul>
                </aside>
            </div>
        </div>
    );
};

export default Docs;